import React, { useState, useEffect } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import "./IntakeForm.scss";
import DatePickerComponent from "./DatePickerComponent";
import GLOBAL from "../../../global";
import {getUserIdentification} from "../../../utils/Utils";
import ExamService from "../../../services/ExamService";

const IntakeForm = (props) => {
    const [clickedButtonId, setClickedButtonId] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await ExamService.getProfile();
                if (response?.success) {
                    // Handle both wrapped { Message: ... } and flat responses
                    const data = response.data?.Message || response.data;
                    if (data && (data.DateOfBirth || data.FirstName || data.LastName)) {
                        setProfileData(data);
                    }
                }
            } catch (error) {
                console.error("Error fetching profile:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchProfile();
    }, []);

    const initialValuesForIntake = () => {
        let dob = profileData?.DateOfBirthValue || profileData?.DateOfBirth || profileData?.dateOfBirth || null;
        let firstName = profileData?.FirstName || profileData?.firstName || "";
        let lastName = profileData?.LastName || profileData?.lastName || "";
        let fullName = profileData?.FullName || profileData?.fullName || `${firstName} ${lastName}`.trim();

        let patientDOB = null;
        if (dob) {
            if (dob instanceof Date) {
                patientDOB = dob;
            } else if (typeof dob === 'string') {
                const dateParts = dob.split(/[-T/ ]/);
                if (dateParts.length >= 3) {
                    // Check if year is first (YYYY-MM-DD or YYYY/MM/DD)
                    if (dateParts[0].length === 4) {
                        patientDOB = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                    } else if (dateParts[2].length === 4) {
                        // Assuming MM/DD/YYYY as default if year is last
                        patientDOB = new Date(dateParts[2], dateParts[0] - 1, dateParts[1]);
                    } else {
                        patientDOB = new Date(dob);
                    }
                } else {
                    patientDOB = new Date(dob);
                }
            }
        }

        return {
            PatientName: fullName,
            PatientDateOfBirth: patientDOB,
            PatientSignature: "",
            Today: new Date(),
            PersonalRepresentative: "",
            Date: new Date(),
            terms_and_condition: false,
        };
    };

    const validationSchema = () => {
        return Yup.object().shape({
            PatientName: Yup.string().required("This field is required"),
            PatientDateOfBirth: Yup.date()
                .required("This field is required")
                .typeError("This field is required"),
            PatientSignature: Yup.string().required("This field is required"),
            Today: Yup.date()
                .required("This field is required")
                .typeError("This field is required"),
            PersonalRepresentative: Yup.string().required("This field is required"),
            Date: Yup.date()
                .required("This field is required")
                .typeError("This field is required"),
            terms_and_condition: Yup.boolean().oneOf(
                [true],
                "You must accept the terms and conditions"
            ),
        });
    };

    const onSubmitDocument = (values) => {
        if (clickedButtonId === "JOIN_NOW") {
            props.onClickJoinTelemedicineNow(values);
        } else if (clickedButtonId === "JOIN_LATER") {
            props.onClickJoinTelemedicineLater(values);
        } else if(clickedButtonId === "SUBMIT") {
            props.onClickSubmitIntakeForm(values);
        }
    };

    if (isLoading) {
        return null;
    }

    return (
        <div className="intake-form-container">
            <Formik
                enableReinitialize={true}
                initialValues={initialValuesForIntake()}
                onSubmit={onSubmitDocument}
                validationSchema={validationSchema}
            >
                {({
                      values,
                      errors,
                      touched,
                      handleSubmit,
                      handleChange,
                      handleBlur,
                      // isSubmitting,
                  }) => (
                    <Form
                        className="intake-form"
                        onSubmit={(e) => {
                            const { id } = e.nativeEvent.submitter;
                            setClickedButtonId(id);
                            return handleSubmit(e);
                        }}
                    >
                        {/* Section 1 */}
                        <div className="form-section">
                            <h3 className="section-header">
                                {`${getUserIdentification()} Acknowledgement`}
                            </h3>
                            <p className="section-text">
                                My signature verifies that I have been provided a copy of My MedicalHUB. I
                                understand that if I would like a copy of this Notice, My MedicalHUB will
                                provide me with a copy of this document.
                            </p>

                            <div className="form-grid">
                                {/* Patient Name */}
                                <div className="form-group">
                                    <label htmlFor="PatientName">{`${getUserIdentification()}'s name*`}</label>
                                    <input
                                        id="PatientName"
                                        name="PatientName"
                                        type="text"
                                        value={values.PatientName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className="form-input"
                                    />
                                    {errors.PatientName && touched.PatientName && (
                                        <div className="error-msg">{errors.PatientName}</div>
                                    )}
                                </div>

                                {/* Date of Birth */}
                                <div className="form-group">
                                    <label htmlFor="PatientDateOfBirth">{`${getUserIdentification()}'s date of birth`}</label>
                                    <DatePickerComponent
                                        name="PatientDateOfBirth"
                                        design="form-input"
                                    />
                                    {errors.PatientDateOfBirth && touched.PatientDateOfBirth && (
                                        <div className="error-msg">{errors.PatientDateOfBirth}</div>
                                    )}
                                </div>

                                {/* Signature */}
                                <div className="form-group">
                                    <label htmlFor="PatientSignature">{`${getUserIdentification()}'s signature*`}</label>
                                    <input
                                        id="PatientSignature"
                                        name="PatientSignature"
                                        type="text"
                                        value={values.PatientSignature}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className="form-input"
                                    />
                                    {errors.PatientSignature && touched.PatientSignature && (
                                        <div className="error-msg">{errors.PatientSignature}</div>
                                    )}
                                </div>

                                {/* Today */}
                                <div className="form-group">
                                    <label htmlFor="Today">Today</label>
                                    <DatePickerComponent name="Today" design="form-input" />
                                    {errors.Today && touched.Today && (
                                        <div className="error-msg">{errors.Today}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Section 2 */}
                        <div className="form-section">
                            <h3 className="section-header">
                                {`${getUserIdentification()} Information`}
                            </h3>
                            <p className="section-text">
                                I understand that I have the right to revoke this authorization at any
                                time and that I have the right to inspect or copy the protected health
                                information to be disclosed as described in this document.
                            </p>
                            <p className="section-text">
                                I understand that a revocation is not effective in cases where the
                                information has already been disclosed but will be effective going
                                forward.{" "}
                                <strong>
                                    This authorization shall be in effect until revoked by the{" "}
                                    {getUserIdentification().toLowerCase()}.
                                </strong>
                            </p>

                            <div className="form-grid">
                                {/* Representative Signature */}
                                <div className="form-group">
                                    <label htmlFor="PersonalRepresentative">
                                        {`Signature of ${getUserIdentification()} or Personal Representative*`}
                                    </label>
                                    <input
                                        id="PersonalRepresentative"
                                        name="PersonalRepresentative"
                                        type="text"
                                        value={values.PersonalRepresentative}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        className="form-input"
                                    />
                                    {errors.PersonalRepresentative && touched.PersonalRepresentative && (
                                        <div className="error-msg">{errors.PersonalRepresentative}</div>
                                    )}
                                </div>

                                {/* Date */}
                                <div className="form-group">
                                    <label htmlFor="Date">Date</label>
                                    <DatePickerComponent name="Date" design="form-input" />
                                    {errors.Date && touched.Date && (
                                        <div className="error-msg">{errors.Date}</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Terms & Submit */}
                        <div className="form-footer">
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    name="terms_and_condition"
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    checked={values.terms_and_condition}
                                />
                                I agree to consent{" "}
                                <span
                                    onClick={props.termsAndConditionModalHandler}
                                    className="link-text"
                                >
                  Terms & Services
                </span>{" "}
                                to telemedicine
                            </label>
                            {errors.terms_and_condition && touched.terms_and_condition && (
                                <div className="error-msg">{errors.terms_and_condition}</div>
                            )}

                            <div className="button-group">{props.children}</div>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default IntakeForm;
